// Dot-separated labels of letters, digits and inner hyphens. URL() has already
// lowercased the host and turned an internationalized name into punycode.
const HOSTNAME =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

// Strips protocol, `www.`, path, query, port and trailing dots so that values
// like `https://www.iluxmotors.com/about` collapse to `iluxmotors.com`. logo.dev
// (and most logo APIs) only accept a bare hostname — anything else 404s.
// Returns null for text with no hostname in it ("not a domain!!", "acme"), so
// callers that validate on a null (the company domain filter) reject it
// instead of searching for it.
export function normalizeDomain(input?: string | null): string | null {
  if (!input) return null;

  let value = input.trim();
  if (!value) return null;

  // URL parser handles schemes like `https://`, `http://`, even `mailto:`.
  // Add a default scheme when missing so URL() can pull the hostname out.
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
    value = `https://${value}`;
  }

  let hostname: string;
  try {
    hostname = new URL(value).hostname;
  } catch {
    // Last-resort manual parse for inputs URL() rejects (rare).
    hostname = input
      .trim()
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .split(':')[0];
  }

  hostname = hostname
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.+$/, '');
  return HOSTNAME.test(hostname) ? hostname : null;
}

// Every wire column that holds a domain holds a BARE hostname, but the values
// reaching those columns come from saved searches, ?q= links, CRM records and
// agent-authored filters — so they can be raw pastes. Non-strings, blanks and
// non-hostnames drop: a CRM placeholder like "unverified" isn't a domain, and
// the people search provider rejects the whole request over one (Replay:
// "Search failed" 502s on every retry). First-seen order is
// kept. `normalizeDomain` already lowercases, so the Set dedupes
// case-insensitively by construction.
export function normalizeDomainList(values: readonly unknown[]): string[] {
  const out = new Set<string>();
  for (const value of values) {
    const domain = typeof value === 'string' ? normalizeDomain(value) : null;
    if (domain) out.add(domain);
  }
  return Array.from(out);
}
